import { UNITED_STATES, countryFromParam, isUsState, normalizedCountry, normalizedState, titleCase } from "./places";

/**
 * What the bulk upload of sent cards (#56) can work out before Joey reviews a card: the
 * details in a Notebloc doc title, which photos belong together, and the day a photo was
 * taken. Everything here is a first guess she checks on the review list.
 */

/**
 * The fields a doc title can fill in, named as the server reads them, so the upload sends
 * them as they are. Under other names (forumThread…) the server dropped them (PR #73).
 */
export type TitleDetails = {
  recipientUser: string;
  recipientName: string;
  country: string;
  region: string;
  thread: string;
  group: string;
  topic: string;
  acquisitionType: string;
  acquisitionDetail: string;
};

const EMPTY_DETAILS: TitleDetails = {
  recipientUser: "", recipientName: "", country: "", region: "",
  thread: "", group: "", topic: "", acquisitionType: "", acquisitionDetail: "",
};

/**
 * The most the server takes in each field (NormalizeSentFields, NormalizeForumThread). A
 * title with no bracket goes whole into the thread; past the limit the server would refuse
 * the upload, and picking the file again would fail the same way, so it is cut instead.
 */
const TITLE_FIELD_LIMITS: Record<keyof TitleDetails, number> = {
  recipientUser: 60, recipientName: 120, country: 80, region: 80,
  thread: 120, group: 40, topic: 80, acquisitionType: 40, acquisitionDetail: 40,
};

/**
 * Round robins are the only threads with numbered groups (#54), so a group is only looked
 * for after an "RR". A tag or lottery title with a number in it, like "2026 Calendar Tag",
 * stays whole rather than losing its year to a group box.
 *
 * The group is a number, or a letter, a dash and a number: "356", "#2127", "A-53".
 * "Group" before it is optional, and so is a dash between the thread and the group, since
 * Joey writes both "Mini RR A-53" and "Mini RR - A-53". What follows is the topic, with or
 * without brackets: "Birds", "(Decorated)".
 */
const ROUND_ROBIN_GROUP = /^(.*?\b(?:RR|round\s+robin))\s*(?:-\s*)?(?:group\s*)?#?\s*([a-z]-?\d+|\d+)\b\s*(.*)$/i;

function detailFor(thread: string) {
  if (/\bRR\b|round\s+robin/i.test(thread)) return "round-robin";
  if (/\btags?\b/i.test(thread)) return "tag";
  if (/\blottery\b/i.test(thread)) return "lottery";
  if (/\bswaps?\b/i.test(thread)) return "swap";
  return "";
}

/**
 * Where a card went, from what Joey writes in the title: a US state (usually its postal
 * code) or a country.
 */
function placeFrom(written: string) {
  if (!written) return { country: "", region: "" };
  if (isUsState(written)) return { country: "United States", region: titleCase(normalizedState(written)) };
  return { country: written, region: "" };
}

/**
 * "…)_2" and "…) (1)": Notebloc numbers a title used twice, and a browser numbers a file
 * downloaded twice. Left on, they hid the bracket and the whole title went into the thread.
 */
const REPEAT_MARK = /(\)|#\s*\d+|outgoing)\s*(?:_\d+|\(\d+\))$/i;

/**
 * The bracket at the end of a title and what comes before it. A bracket inside it, as in
 * "Germany (Föhr Island)", stays inside, and one never closed runs to the end.
 */
function lastBracket(title: string): { head: string; inside: string } | null {
  if (title.endsWith(")")) {
    let depth = 0;
    for (let index = title.length - 1; index >= 0; index -= 1) {
      if (title[index] === ")") depth += 1;
      else if (title[index] === "(" && (depth -= 1) === 0) {
        return { head: title.slice(0, index).trim(), inside: title.slice(index + 1, -1) };
      }
    }
    return null;
  }
  const open = title.lastIndexOf("(");
  return open !== -1 && !title.slice(open).includes(")") ? { head: title.slice(0, open).trim(), inside: title.slice(open + 1) } : null;
}

/** "Danielle Wick (LunaUrsa) outgoing #2": a penpal, friend or family, and which mailing to them. */
const OUTGOING = /^(.*?)\s*(?:\(([^()]*)\))?\s*outgoing\s*(?:#\s*\d+)?\s*$/i;

/**
 * Reads a Notebloc doc title, as it arrives in the exported PDF's file name.
 *
 * Joey titles forum docs "Thread (recipient name - state if US, otherwise country -
 * username)", e.g. "Mini RR A-53 Birds (Theo Martin - NH - quietcove)". Notebloc turns a
 * "/" in the title into "_" in the file name, so the thread part gets its slashes back.
 * Only the thread part: usernames are full of underscores that were never slashes.
 *
 * Penpals, friends and family are "Name (username) outgoing #2", the second mailing to
 * them, with no thread. She's less consistent with these and fixes them on the review list.
 */
export function parseDocTitle(fileName: string): TitleDetails {
  const details = readDocTitle(fileName);
  for (const name of Object.keys(details) as (keyof TitleDetails)[]) {
    const letters = [...details[name]];
    if (letters.length > TITLE_FIELD_LIMITS[name]) details[name] = letters.slice(0, TITLE_FIELD_LIMITS[name]).join("").trim();
  }
  return details;
}

function readDocTitle(fileName: string): TitleDetails {
  const title = fileName.replace(/\.pdf$/i, "").trim().replace(REPEAT_MARK, "$1");
  const outgoing = title.match(OUTGOING);
  if (outgoing?.[1].trim()) {
    const user = (outgoing[2] ?? "").trim().replace(/^@+/, "");
    return { ...EMPTY_DETAILS, recipientName: outgoing[1].trim(), recipientUser: /^\S+$/.test(user) ? user : "" };
  }
  const bracketed = lastBracket(title);
  const head = (bracketed ? bracketed.head : title).replace(/_/g, "/").trim();
  const parts = bracketed ? bracketed.inside.split(/\s+-\s+/).map((part) => part.trim()) : [];

  const details: TitleDetails = { ...EMPTY_DETAILS, thread: head };
  const group = head.match(ROUND_ROBIN_GROUP);
  if (group) {
    details.thread = group[1].trim();
    details.group = group[2];
    details.topic = group[3].replace(/^\(\s*|\s*\)$/g, "").trim();
  }
  details.acquisitionDetail = detailFor(details.thread);
  if (details.acquisitionDetail) details.acquisitionType = "postcrossing-forum";

  // Three parts is the pattern. A name with " - " in it would make more, so the first part
  // is the name, the last the username, and everything between is the place.
  if (parts.length >= 3) {
    details.recipientName = parts[0];
    details.recipientUser = parts[parts.length - 1].replace(/^@+/, "");
    Object.assign(details, placeFrom(parts.slice(1, -1).join(" - ")));
  } else if (parts.length === 2) {
    details.recipientName = parts[0];
    Object.assign(details, placeFrom(parts[1]));
  } else if (parts.length === 1) {
    details.recipientName = parts[0];
  }
  return details;
}

/** A photo waiting to be paired, with the time it was taken if the upload could read it. */
export type PickedPhoto = { id: string; name: string; takenAt: number | null };

/** One card made of photos: the front, and the back unless the front stands alone. */
export type PhotoPair<P extends PickedPhoto = PickedPhoto> = { front: P; back: P | null };

const byName = new Intl.Collator(undefined, { numeric: true }).compare;

/**
 * Pairs photos into cards: in the order they were taken, each front with the photo taken
 * just after it. Joey always scans the front first.
 *
 * `singles` are photos she has said are a card on their own, a front whose back is
 * missing. Everything after one re-pairs from there, which is what fixes a whole batch
 * when a single scan is missing in the middle. A photo left over at the end is a single
 * too. A photo with no time sorts after those with one, by name.
 */
export function pairPhotos<P extends PickedPhoto>(photos: readonly P[], singles: ReadonlySet<string> = new Set()): PhotoPair<P>[] {
  const ordered = [...photos].sort((a, b) => {
    if (a.takenAt !== b.takenAt) {
      if (a.takenAt === null) return 1;
      if (b.takenAt === null) return -1;
      return a.takenAt - b.takenAt;
    }
    return byName(a.name, b.name);
  });
  const pairs: PhotoPair<P>[] = [];
  for (let index = 0; index < ordered.length;) {
    const front = ordered[index];
    const next = ordered[index + 1];
    if (!next || singles.has(front.id)) {
      pairs.push({ front, back: null });
      index += 1;
    } else {
      pairs.push({ front, back: next });
      index += 2;
    }
  }
  return pairs;
}

/** The day a photo was taken, as the sent date field writes it. Joey: "The sent date is almost always the day the photo was taken." */
export function sentDateFrom(takenAt: number | null) {
  if (takenAt === null) return "";
  const day = new Date(takenAt);
  const pad = (n: number) => String(n).padStart(2, "0");
  return `${day.getFullYear()}-${pad(day.getMonth() + 1)}-${pad(day.getDate())}`;
}

/** The parts of a sent card the lookup searches. */
export type SearchableSentCard = {
  recipientUser: string;
  recipientName: string;
  thread: string;
  group: string;
  topic: string;
  country: string;
  region: string;
};

/**
 * One search box for a username, a real name, a thread or group, or a place (Joey: "One
 * search box is enough"). "@fjord", "maya", "WBMP RR", "356", "wbmp rr 356" and "sweden"
 * all find a card sent in WBMP RR group 356 to Maya, @fjordpaper, in Sweden.
 *
 * A country is also matched by name, folded as the map folds it: the server stores
 * "United States", which "USA" is not a piece of, and the map labels it "United States Of
 * America", which is not a piece of it either.
 */
export function matchesSentCard(card: SearchableSentCard, typed: string) {
  const words = typed.trim().replace(/^@+/, "").toLowerCase();
  if (!words) return true;
  const fields = [
    card.recipientUser, card.recipientName, card.thread, card.group, card.topic, card.country, card.region,
    `${card.thread} ${card.group}`, `${card.thread} group ${card.group}`,
  ];
  return fields.some((field) => field.toLowerCase().includes(words)) ||
    (card.country.trim() !== "" && countryFromParam(words) === countryFromParam(card.country));
}

/**
 * Whether a mailing went to the place a map link names: ?country= or ?region=, a US state.
 * Folded the way the map counts (countryFromParam, normalizedState), so "Germany · 41" on
 * the map opens forty-one mailings here, not a search for the word, which would also
 * answer "Virginia" with West Virginia's.
 */
export function sentToPlace(card: Pick<SearchableSentCard, "country" | "region">, kind: "country" | "region", place: string) {
  return kind === "country"
    ? countryFromParam(card.country) === place
    : normalizedCountry(card.country) === UNITED_STATES && normalizedState(card.region) === place;
}

/* ---------- what each page of a mailing is (#63, #65) ---------- */

/** What one page of a mailing is, as she picks it on the review list. */
export type PageRole = "env-front" | "env-back" | "front" | "back" | "letter" | "extras" | "skip";

export type MailingKind = "envelope" | "postcard" | "letter" | "extras";

/** One item of a mailing, holding pages by their number in the doc. */
export type LayoutItem = { kind: MailingKind; pages: { page: number; side: "front" | "back" | "" }[] };

/** Taller than wide by about a sheet of paper's proportion: A4 and US letter both pass. */
const isPaperShaped = (size: { width: number; height: number } | null) => Boolean(size && size.height / size.width >= 1.25);

/**
 * A first guess at what each page of a Notebloc doc is, from the order Joey scans in and
 * each page's shape. She fixes anything it gets wrong before saving.
 *
 * - One page is a postcard front; two are a postcard, front then back.
 * - Otherwise page 1 is the envelope front, unless it is shaped like paper, which means
 *   there was no envelope.
 * - The first run of paper-shaped pages is the letter, and anything after it is the extras.
 * - The card-shaped pages before the letter pair up front-then-back. An even number of
 *   them, counting the envelope, means the envelope's back was scanned too.
 *
 * Tune these against her real docs, looked at locally only: they show addresses.
 *
 * Not guessed, and fixed by hand on the review list:
 * - A portrait postcard reads as a letter page: it is shaped like paper.
 * - A letter written across the cards' backs, scanned as every front and then every
 *   back. She keeps it as the fronts plus one letter. Page shape can't tell it from an
 *   envelope back, a letter and extras, or from portrait cards in a block (review of #72).
 */
export function guessRoles(sizes: ({ width: number; height: number } | null)[]): PageRole[] {
  const n = sizes.length;
  if (n === 0) return [];
  if (n === 1) return ["front"];
  if (n === 2) return ["front", "back"];

  const paper = sizes.map(isPaperShaped);
  let letterStart = paper.indexOf(true);
  if (letterStart === -1) letterStart = n;
  let letterEnd = letterStart;
  while (letterEnd < n && paper[letterEnd]) letterEnd += 1;

  const roles: PageRole[] = Array(n).fill("extras");
  for (let page = letterStart; page < letterEnd; page += 1) roles[page] = "letter";
  if (letterStart === 0) return roles;

  roles[0] = "env-front";
  let page = 1;
  if (letterStart % 2 === 0) roles[page++] = "env-back";
  for (; page < letterStart; page += 1) roles[page] = (page - (letterStart % 2 === 0 ? 2 : 1)) % 2 === 0 ? "front" : "back";
  return roles;
}

/**
 * The items a mailing holds, from what she says each page is, in page order: a card's back
 * goes with the front before it, letter pages make one letter, extras photos one extras,
 * and the envelope's two sides one envelope. Pages left out are in no item.
 *
 * `problems` names the pages that can't go anywhere, like a second envelope front; the
 * mailing can't be saved until she changes them.
 */
export function itemsFromRoles(roles: PageRole[]): { items: LayoutItem[]; labels: string[]; problems: Set<number> } {
  const items: LayoutItem[] = [];
  const labels: string[] = [];
  const problems = new Set<number>();
  const cards: LayoutItem[] = [];
  const single = (kind: MailingKind) => {
    let item = items.find((candidate) => candidate.kind === kind);
    if (!item) { item = { kind, pages: [] }; items.push(item); }
    return item;
  };
  roles.forEach((role, page) => {
    if (role === "front") {
      const card: LayoutItem = { kind: "postcard", pages: [{ page, side: "front" }] };
      items.push(card);
      cards.push(card);
      labels[page] = `Card ${cards.length} · front`;
    } else if (role === "back") {
      let card = cards[cards.length - 1];
      if (!card || card.pages.some((p) => p.side === "back")) {
        card = { kind: "postcard", pages: [] };
        items.push(card);
        cards.push(card);
      }
      card.pages.push({ page, side: "back" });
      labels[page] = `Card ${cards.indexOf(card) + 1} · back`;
    } else if (role === "env-front" || role === "env-back") {
      const side = role === "env-front" ? "front" : "back";
      const envelope = single("envelope");
      if (envelope.pages.some((p) => p.side === side)) {
        problems.add(page);
        labels[page] = `A second envelope ${side}?`;
      } else {
        envelope.pages.push({ page, side });
        envelope.pages.sort((a, b) => (a.side === "back" ? 1 : 0) - (b.side === "back" ? 1 : 0));
        labels[page] = `Envelope · ${side}`;
      }
    } else if (role === "letter") {
      const letter = single("letter");
      letter.pages.push({ page, side: "" });
      labels[page] = `Letter p.${letter.pages.length}`;
    } else if (role === "extras") {
      const extras = single("extras");
      extras.pages.push({ page, side: "" });
      labels[page] = extras.pages.length === 1 ? "Extras" : `Extras ${extras.pages.length}`;
    } else {
      labels[page] = "Left out";
    }
  });
  return { items, labels, problems };
}

/** What each page is, read back from a saved mailing's items. Pages in no item are left out. */
export function rolesFromItems(items: { kind: string; pages: { page: number; side: string }[] }[], pageCount: number): PageRole[] {
  const roles: PageRole[] = Array(pageCount).fill("skip");
  for (const item of items) {
    for (const { page, side } of item.pages) {
      if (page < 0 || page >= pageCount) continue;
      if (item.kind === "envelope") roles[page] = side === "back" ? "env-back" : "env-front";
      else if (item.kind === "postcard") roles[page] = side === "back" ? "back" : "front";
      else if (item.kind === "letter") roles[page] = "letter";
      else roles[page] = "extras";
    }
  }
  return roles;
}

/** "Envelope · 2 postcards · 3-page letter · Extras". */
export function mailingTally(items: { kind: string; pages: unknown[] }[]) {
  const parts: string[] = [];
  if (items.some((item) => item.kind === "envelope")) parts.push("Envelope");
  const cards = items.filter((item) => item.kind === "postcard").length;
  if (cards) parts.push(`${cards} postcard${cards === 1 ? "" : "s"}`);
  const letter = items.find((item) => item.kind === "letter");
  if (letter) parts.push(`${letter.pages.length}-page letter`);
  if (items.some((item) => item.kind === "extras")) parts.push("Extras");
  return parts.join(" · ") || "Nothing kept";
}

/**
 * Every photo of a mailing in the order the mailing view shows them, each with what it
 * is: item by item, a front before its back, a letter's pages in order.
 */
export function mailingPhotos<P extends { side: string; position: number }>(items: { kind: MailingKind; photos: P[] }[]) {
  const out: { photo: P; caption: string }[] = [];
  let card = 0;
  for (const item of items) {
    if (item.kind === "envelope" || item.kind === "postcard") {
      if (item.kind === "postcard") card += 1;
      const name = item.kind === "envelope" ? "Envelope" : `Postcard ${card}`;
      for (const side of ["front", "back"]) {
        const photo = item.photos.find((p) => p.side === side);
        if (photo) out.push({ photo, caption: `${name} · ${side}` });
      }
      continue;
    }
    const pages = [...item.photos].sort((a, b) => a.position - b.position);
    const name = item.kind === "letter" ? "Letter" : "Extras";
    const each = item.kind === "letter" ? "page " : "";
    pages.forEach((photo, index) => out.push({
      photo,
      caption: pages.length > 1 ? `${name} · ${each}${index + 1} of ${pages.length}` : name,
    }));
  }
  return out;
}

/** The hex SHA-256 of a scan as it came off the PDF or the phone, which names it to the server. */
export async function sha256Hex(bytes: BufferSource) {
  const digest = new Uint8Array(await crypto.subtle.digest("SHA-256", bytes));
  return Array.from(digest, (byte) => byte.toString(16).padStart(2, "0")).join("");
}

/**
 * Leaves out a page that is the very same scan as an earlier one. The server names pages
 * by their hash, so a doc with one scan twice could never be uploaded otherwise, and
 * picking it again would fail the same way.
 */
export function skipRepeatedScans(roles: PageRole[], hashes: string[]): PageRole[] {
  return roles.map((role, page) => hashes.indexOf(hashes[page]) < page ? "skip" : role);
}
